import type { TeamMember } from '@api'
import { Trigger as PrimitiveTrigger } from '@radix-ui/react-select'

import IconLoading from '@/assets/icons/icon-loading.svg'
import { Select, SelectContent, SelectItem } from '@/components/ui/select.tsx'
import LogoBlack from '@/modules/chatting/icons/logo-black.svg'
import SmallCheckIcon from '@/modules/chatting/icons/small-check.svg'
import { classNames } from '@/utils/helpers.ts'

interface Props {
  handleChangeAssignee: (memberId: string) => void
  assignedUserId: number
  originallyAssignedUserId: number
  canDropdownOpen: boolean
  trigger: JSX.Element
  members: TeamMember[]
  useDelimiter?: boolean
  isLoading?: boolean
}

export const ReassignDropdown = ({
  handleChangeAssignee,
  assignedUserId,
  originallyAssignedUserId,
  canDropdownOpen,
  trigger,
  members,
  useDelimiter = true,
  isLoading,
}: Props) => {
  return (
    <>
      {useDelimiter && <span className="text-secondary-400">&#x2022;</span>}
      {isLoading ? (
        <div className="flex items-center gap-2">
          <IconLoading className="text-secondary-300 h-4 w-4 animate-spin" />{' '}
          Loading...
        </div>
      ) : (
        <Select
          onValueChange={handleChangeAssignee}
          value={String(assignedUserId)}
        >
          <PrimitiveTrigger
            className={classNames(
              'text-title flex h-max items-center gap-1 overflow-hidden whitespace-nowrap p-0 outline-none',
              {
                'hover:text-primary-500': canDropdownOpen,
              },
            )}
            disabled={!canDropdownOpen}
          >
            {trigger} <LogoBlack className="h-4 w-4 shrink-0 text-white" />
          </PrimitiveTrigger>
          <SelectContent
            position="popper"
            align="end"
            className="relative max-h-[50vh] min-w-[200px] max-w-[300px]"
            header={
              <p className="text-secondary-300 dark:bg-secondary-500 w-full bg-white px-2 py-1.5 text-sm">
                Re-assign to
              </p>
            }
          >
            {members?.map((member) => {
              const isSelected = member.userId === assignedUserId
              const isOriginallyAssigned =
                member.userId === originallyAssignedUserId

              return (
                <SelectItem
                  key={member.userId}
                  value={String(member.userId)}
                  className={classNames(
                    'max-w-full overflow-hidden rounded-md font-medium',
                    {
                      'bg-primary-500 hover:bg-primary-500 text-white':
                        isSelected,
                    },
                  )}
                >
                  <div className="flex max-w-full items-center justify-between gap-4 overflow-hidden">
                    <span className="flex gap-1 overflow-hidden">
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                        {member.name}
                      </span>
                      {isOriginallyAssigned && (
                        <span
                          className={classNames('text-secondary-400 shrink-0', {
                            'text-secondary-100': isSelected,
                          })}
                        >
                          (originally)
                        </span>
                      )}
                    </span>
                    {isSelected && <SmallCheckIcon className="shrink-0" />}
                  </div>
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      )}
    </>
  )
}
